import React from 'react';
import { Card, CardContent, CardMedia, Typography, Box, Chip, Avatar } from '@mui/material';
import { Link } from 'react-router-dom';
import { Video, getFullThumbnailUrl } from '../services/api';
import { usePeerTube } from '../contexts/PeerTubeContext';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

interface VideoCardProps {
  video: Video;
}

const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  }).format(date);
};

const VideoCard: React.FC<VideoCardProps> = ({ video }) => {
  const { selectedInstance } = usePeerTube();
  return (
    <Card 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        transition: 'all 0.3s ease',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 6px 16px rgba(0,0,0,0.08)',
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: '0 12px 20px rgba(0,0,0,0.15)'
        }
      }}
      component={Link}
      to={`/video/${video.uuid}`}
      style={{ textDecoration: 'none' }}
    >
      <Box sx={{ position: 'relative' }}>
        <CardMedia
          component="img"
          height="180"
          image={getFullThumbnailUrl(video.thumbnailPath, selectedInstance.url)}
          alt={video.name}
          sx={{ 
            objectFit: 'cover',
            transition: 'transform 0.5s ease',
            '&:hover': {
              transform: 'scale(1.05)'
            }
          }}
        />
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(0,0,0,0.2)',
          opacity: 0,
          transition: 'opacity 0.3s ease',
          '&:hover': {
            opacity: 1
          }
        }}>
          <Box sx={{
            width: 60,
            height: 60,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255,255,255,0.9)',
            boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
            transform: 'scale(0.8)',
            transition: 'transform 0.3s ease',
            '&:hover': {
              transform: 'scale(1)'
            }
          }}>
            <PlayArrowIcon sx={{ fontSize: 36, color: 'primary.main' }} />
          </Box>
        </Box>
        <Chip
          icon={<PlayArrowIcon sx={{ fontSize: 16, color: 'white !important' }} />}
          label={formatDuration(video.duration)}
          size="small"
          sx={{
            position: 'absolute',
            bottom: 8,
            right: 8,
            bgcolor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            fontWeight: 500,
            backdropFilter: 'blur(4px)',
            '& .MuiChip-label': {
              px: 1
            }
          }}
        />
        {video.channel && (
          <Avatar
            src={video.channel.avatarPath ? getFullThumbnailUrl(video.channel.avatarPath, selectedInstance.url) : undefined}
            alt={video.channel.displayName}
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              width: 32,
              height: 32,
              border: '2px solid white',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}
          >
            {video.channel.displayName.charAt(0)}
          </Avatar>
        )}
      </Box>
      <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
        <Typography gutterBottom variant="h6" component="div" sx={{ 
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          lineHeight: '1.3em',
          height: '2.6em',
          fontWeight: 600,
          fontSize: '1rem',
          color: '#1a1a1a'
        }}>
          {video.name}
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          mt: 1,
          mb: 0.5,
          color: 'text.secondary'
        }}>
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
            {video.channel?.displayName || 'Unknown Channel'}
          </Typography>
        </Box>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          mt: 2,
          pt: 1.5,
          borderTop: '1px solid rgba(0,0,0,0.06)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <VisibilityIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary', opacity: 0.8 }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              {video.views.toLocaleString()}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CalendarTodayIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary', opacity: 0.8 }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              {formatDate(video.publishedAt)}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default VideoCard;
